import {
  stroot,
  auth,
  user,
  organisationFeatures as orgFeaturesAPI,
} from '@birdi/js-sdk';
import { formatName } from '@birdi/utils/src/text';
import { getFirstPromoterData } from '@birdi/first-promoter';
import { posthog } from '../lib/posthog';
import { store } from '../state/index';
import { updateMeta, loadMeta } from '../meta/reducer';
// Removed history import - using window.location for navigation in non-component code
import pino from '../lib/pino';
import {
  AUTHORISING,
  AUTHORISE,
  ANONYMOUS,
  ERROR,
  UPDATE_FIELD,
} from './reducer';
import { syncFirstPromoter } from '@birdi/js-sdk/src/public/user';

/**
 * setUserAndMetaState called when authenticating
 * from an auth flow (e.g. email login, google login, reset password)
 * or loading the app initially
 * @param {function} dispatch redux dispatcher
 */
function setUserAndMetaState(dispatch) {
  return Promise.all([
    user
      .getMeta(stroot('xof9ci'))
      .then((response) => {
        dispatch(loadMeta(response.body));
        dispatch({ type: AUTHORISE });
        // Identify user in analytics etc here
        const intercomIdentity = {
          app_id: 'b0ht41y7',
          user_id: response.body.id,
          user_hash: response.body.intercomVerification,
          name: `${process.env.INTERCOM_PREFIX === true ? '[test] ' : ''}${formatName(response.body.fname, response.body.lname)}`,
          email: response.body.email,
          meta: response.body.organisation.planV2,
          job_notifications: response.body.jobNotifications,
          ...(response.body.scopeRole && { scope_role: response.body.scopeRole }),
          ...(response.body.industry && { Industry: response.body.industry }), // Intercom attribute name is "Industry" (capitalized)
          company: {
            company_id: response.body.organisation.id,
            name: response.body.organisation.name,
            plan: response.body.organisation.planV2,
          },
        };
        // Set intercomSettings so Intercom will use it when it loads (as per intercom/index.ts comment)
        if (!window.intercomSettings) {
          window.intercomSettings = {};
        }
        Object.assign(window.intercomSettings, intercomIdentity);
        // Also call update if Intercom is already loaded
        if (window?.Intercom) {
          window.Intercom('update', intercomIdentity);
        }
        window._hsq?.push(['identify', { email: response.body.email }]);
        posthog?.identify(
          response.body.id,
          {},
          {
            name: formatName(response.body.fname, response.body.lname),
            email: response.body.email,
            role: response.body.scopeRole,
          },
        );
        const { organisation } = response.body;
        posthog?.group('organisation', organisation.id, {
          name: organisation.name,
          plan: organisation.planData?.stripeProductName || 'unknown',
          planId: organisation.planV2,
        });
        getFirstPromoterData()
          .then((data) => {
            if (data?.tid) return syncFirstPromoter(stroot('syncfp'), data.tid);
            return false;
          })
          .catch((err) => {
            console.log(err);
          });
        // TODO: review websockets later on
        // if (!process.env.WEBSOCKETS_OFF) websocketInit(dispatch);
        return {
          authenticated: true,
          onboardingCompleted: response.body.onboardingCompleted,
        };
      })
      .catch((err) => {
        if (err.message === 'Network Error') {
          pino.info(
            'Network error when attempting to authenticate with existing JWT',
          );

          // Check if user is on sign-up page with organisationId parameter
          const url = new URL(window.location.href);
          const isSignUpWithOrgId =
            window.location.pathname === '/sign-up' &&
            url.searchParams.has('organisationId');
          if (isSignUpWithOrgId) {
            // If on sign-up with organisationId, clear cookie and set to anonymous
            // but don't redirect - let them stay on sign-up page
            auth.clearCookie(stroot('asdzxb'), false);
            dispatch({ type: ANONYMOUS });
            return { authenticated: false, onboardingCompleted: false };
          }

          // Otherwise, redirect to sign-in as before
          window.location.href = `/sign-in${url.search}`;
        }
        if (err.status === 401) {
          auth.clearCookie(stroot('asdzxb'), false);
          dispatch({ type: ANONYMOUS });
        }
        console.log(err);
        return { authenticated: false, onboardingCompleted: false };
      }),

    orgFeaturesAPI
      .getAll(stroot('aa4azm'))
      .then((res) => {
        dispatch(
          updateMeta({
            orgFeatureFlags: res.body,
          }),
        );
      })
      .catch((err) => {
        // TODO: Let org features be consumed by meta state API
      }),
    // features.getByName(FeatureFlagNames.VolumetricRequest)
    //   .then((res) => {
    //     if (res.body.enabled) setBookingEnabled(true);
    //   }).catch((err) => console.log(err));
  ]).then(([{ authenticated, onboardingCompleted }]) => {
    // If user already in onboarding flow, don't kick them out
    // Match both /onboarding and /onboarding/... routes
    const inOnboardingFlow = /^\/onboarding(\/.*)?$/.test(
      window.location.pathname,
    );
    const unauthenticatedOnlyRoutes = [
      /^\/sign-in\/?.*$/,
      /^\/sign-up\/?.*$/,
      /^\/onboarding(\/.*)?$/,  // Match both /onboarding and /onboarding/...
      /^\/reset-password\/?.*$/,
      /^\/set-password\/?.*$/,
      /^\/email-verification\/?.*$/,
      /^\/resend-verification\/?.*$/,
      /^\/invited-user-signup\/?.*$/,
    ];
    // Authenticated and is part of onboarding
    if (!onboardingCompleted && authenticated) {
      // Don't redirect if already on an onboarding route
      if (inOnboardingFlow) return;
      window.location.href = '/onboarding';

      // // If already on organisation-setup, don't redirect
      // if (window.location.pathname === '/organisation-setup') return;
      // // Redirect to organisation setup instead of old onboarding flow
      // window.location.href = '/organisation-setup';
    }
    // If redirect already specified
    // TODO: Middleware could be used here instead
    const metaState = store.getState().meta;
    if (metaState.redirect && authenticated) {
      window.location.href = metaState.redirect;
      return;
    }
    // If authenticated & in anonymous only routes
    // TODO: Define this behaviour in the components that are anonymous only
    // rather than as a list here.
    if (
      authenticated &&
      unauthenticatedOnlyRoutes.some((r) => r.test(window.location.pathname))
    ) {
      // Special case: allow sign-up with organisationId parameter (from Stripe payment)
      // even if user appears to be authenticated (might have stale cookie)
      const url = new URL(window.location.href);
      const isSignUpWithOrgId =
        window.location.pathname === '/sign-up' &&
        url.searchParams.has('organisationId');
      if (isSignUpWithOrgId) {
        // Clear the stale cookie and set to anonymous to allow sign-up
        auth.clearCookie(stroot('asdzxb'), false);
        dispatch({ type: ANONYMOUS });
        return; // Don't redirect, allow them to stay on sign-up page
      }
      window.location.href = '/';
      return;
    }

    if (onboardingCompleted && authenticated) {
      const hasV2Plan = !!metaState.organisation.planV2;
      const isAccountLocked = metaState.organisation.accountLocked;
      if (!hasV2Plan && window.location.pathname !== '/billing/options') {
        window.location.href = '/billing/options';
        return;
      } else if (
        isAccountLocked &&
        window.location.pathname !== '/authentication/account-locked'
      ) {
        window.location.href = '/authentication/account-locked';
        return;
      }
    }
    // Default behaviour is to do nothing, i.e. do not change the location
  });
}

/**
 *
 * @param {object} { username, password }
 */
export const signInWithPassword = (obj) => (dispatch) => {
  dispatch({ type: AUTHORISING });
  return auth
    .signInWithPassword(stroot('yo8mol'), obj)
    .then(() => setUserAndMetaState(dispatch))
    .catch((error) => dispatch({ type: ERROR, payload: error }));
};

export const signInWithGoogleToken = (dispatch) => (code) => {
  dispatch({ type: AUTHORISING });
  auth
    .signInWithGoogleIdToken(stroot('ohch1n'), code)
    .then(() => setUserAndMetaState(dispatch))
    .catch((error) => dispatch({ type: ERROR, payload: error }));
};

// Email verification
export const verifyEmailWithTokenAndSignIn =
  (userId, verificationToken) => (dispatch) =>
    auth
      .verifyEmailWithToken(stroot('sahz6f'), userId, verificationToken)
      .then((res) => {
        setUserAndMetaState(dispatch);
      });

export const getVerificationToken = (email) => () =>
  auth.getVerificationToken(stroot('da2ye5'), email);

// Reset password (Get code)
export const sendPasswordResetCodeToEmail = (email) => (dispatch) =>
  auth
    .sendPasswordResetCodeToEmail(stroot('hain4i'), email)
    .then((res) =>
      dispatch({
        type: UPDATE_FIELD,
        payload: { pendingCode: true, codeId: res.body.id },
      }),
    )
    .catch((error) => dispatch({ type: UPDATE_FIELD, payload: { error } }));

// Reset password (& Automatic login)
export const resetPassword = (obj) => (dispatch) =>
  auth
    .resetPassword(stroot('pix1ao'), obj)
    .then(() => setUserAndMetaState(dispatch))
    .catch((error) => dispatch({ type: UPDATE_FIELD, payload: { error } }));

// Complete invited user signup (& Automatic login)
export const completeInvitedSignup = (obj) => (dispatch) =>
  user
    .completeInvitedSignup(stroot('oomae2'), obj)
    .then(() => setUserAndMetaState(dispatch))
    .catch((error) => dispatch({ type: UPDATE_FIELD, payload: { error } }));

// Update auth state
export const updateField = (obj) => (dispatch) =>
  dispatch({ type: UPDATE_FIELD, payload: obj });

/**
 * Assumes initial auth state is good
 * Checks if bad and redirects to homepage
 */
export const getInitialAuthState = () => (dispatch) => {
  // remove plan changes local storage for users with different accounts
  localStorage.removeItem('cancelPlan');
  localStorage.removeItem('localOldPlan');
  // Skip if user is on email-verification screen to prevent race conditions i.e. cookie clearing
  if (/^\/email-verification\/.*$/.test(window.location.pathname)) {
    return;
  }
  return setUserAndMetaState(dispatch);
};
